<script setup>
/* Tela "Simulação e Digitação" — v42: a simulação roda no simulador-api
   (FastAPI fora do Chatwoot); aqui é só criar (POST), fazer polling do estado
   (2,5s enquanto houver banco consultando) e renderizar. A lista da esquerda
   é PERSISTIDA no serviço: todo cliente consultado fica lá.
   Rollback: o fluxo antigo (/clt_simulador) segue intacto na imagem. */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import simuladorApi from 'dashboard/api/simuladorApi';
import SimulacaoLista from './components/SimulacaoLista.vue';
import SimulacaoForm from './components/SimulacaoForm.vue';
import BancosPainel from './components/BancosPainel.vue';
import DigitacaoPanel from './components/DigitacaoPanel.vue';
import { cpfFmt, foneFmt, labelBanco } from './bancos.js';

const form = ref(null);
const simulacoes = ref([]);
const carregandoLista = ref(false);
const buscaAtual = ref('');
const ativaId = ref(null);
const detalhe = ref(null); // { simulacao, bancos } de GET simulacoes/<id>
const simulando = ref(false);

let pollTimer = null;

const pararPoll = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
};

const temPendente = () =>
  (detalhe.value?.bancos || []).some(b =>
    ['aguardando', 'consultando'].includes(b.status)
  );

const carregarLista = async () => {
  carregandoLista.value = true;
  try {
    const { data } = await simuladorApi.get('simulacoes', {
      busca: buscaAtual.value,
    });
    simulacoes.value = data.simulacoes || [];
  } catch (e) {
    // lista indisponível não derruba a tela — o formulário continua útil
    simulacoes.value = [];
  } finally {
    carregandoLista.value = false;
  }
};

const carregarDetalhe = async id => {
  const { data } = await simuladorApi.get(`simulacoes/${id}`);
  if (ativaId.value !== id) return; // operador já mudou de cliente
  detalhe.value = data;
  // reabrir uma simulação SALVA também precisa do leque certo: a caixa vem no
  // próprio registro, não do formulário
  const dono = data?.simulacao?.inboxId;
  if (dono) carregarBancosDaCaixa(dono);
};

const agendarPoll = () => {
  pararPoll();
  if (!ativaId.value || !temPendente()) {
    // terminou: sincroniza a lista (melhor oferta/status mudaram)
    carregarLista();
    return;
  }
  pollTimer = setTimeout(async () => {
    try {
      await carregarDetalhe(ativaId.value);
    } catch (e) {
      /* um poll perdido não é fatal; o próximo tenta de novo */
    }
    agendarPoll();
  }, 2500);
};

/* Bancos que a CAIXA escolhida realmente tem (credencial + token gerado). A
   tela desenhava o catálogo inteiro do produto (SIM_BANKS), então o operador de
   uma caixa via chips e cards de bancos que não são dela e que nunca
   responderiam. Vazio = nenhum banco é desenhado: melhor pedir a caixa do que
   mostrar um leque alheio. */
const bancosDaCaixa = ref([]);

/* Devolve a lista carregada (além de gravar no ref) para o Simular usar
   exatamente a lista DESTA caixa, sem depender de outra carga concorrente. */
const carregarBancosDaCaixa = async inboxId => {
  if (!inboxId) {
    bancosDaCaixa.value = [];
    return [];
  }
  try {
    // SOMA 321 (16/09): `tela: 1` inclui os bancos "só desta tela"
    // (soma_321) — sem ele o simulador-api devolve o leque de sempre
    const { data } = await simuladorApi.get('bancos-habilitados', {
      inbox_id: inboxId,
      formato: 'adapters',
      tela: 1,
    });
    const lista = (data?.bancos || []).map(b => b.id).filter(Boolean);
    bancosDaCaixa.value = lista;
    return lista;
  } catch (e) {
    // sem lista, não inventamos: a tela fica sem bancos em vez de mostrar os
    // de outra caixa
    bancosDaCaixa.value = [];
    return [];
  }
};

const aoSimular = async dados => {
  simulando.value = true;
  const bancos = await carregarBancosDaCaixa(dados?.inbox_id);
  try {
    // v49 (creds por caixa): simulador-api agora EXIGE inbox_id (422 sem).
    // `dados` já vem do SimulacaoForm com `inbox_id` (seletor de caixa) e é
    // repassado CRU pro POST — nada a filtrar/remapear aqui.
    // SOMA 321 (16/09): manda `bancos` = os cards da caixa, senão o soma_321
    // (fora do leque padrão do simulador-api) nunca roda. Lista vazia (falha
    // ao carregar) = não manda, e o simulador-api usa o leque padrão.
    const body = bancos.length ? { ...dados, bancos } : dados;
    const { data } = await simuladorApi.post('simulacoes', body);
    ativaId.value = data.id;
    await carregarDetalhe(data.id);
    agendarPoll();
    carregarLista();
  } catch (e) {
    form.value?.mostrarErro(
      `Falha ao simular: ${e.response?.data?.detail || e.response?.data?.error || e.message}`
    );
  } finally {
    simulando.value = false;
  }
};

const selecionar = async id => {
  ativaId.value = id;
  detalhe.value = null;
  try {
    await carregarDetalhe(id);
    const s = detalhe.value?.simulacao;
    if (s) form.value?.preencher(s);
    agendarPoll();
  } catch (e) {
    useAlert('Não consegui carregar essa simulação');
  }
};

const novaSimulacao = () => {
  pararPoll();
  form.value?.limpar();
  ativaId.value = null;
  detalhe.value = null;
};

const limparTodas = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Apagar TODAS as simulações salvas? Essa ação não tem volta.'))
    return;
  try {
    await simuladorApi.delete('simulacoes');
    novaSimulacao();
    carregarLista();
    useAlert('Simulações apagadas');
  } catch (e) {
    useAlert('Falha ao apagar as simulações');
  }
};

const aoBusca = termo => {
  buscaAtual.value = termo;
  carregarLista();
};

const editarCliente = () => {
  const s = detalhe.value?.simulacao;
  if (s) form.value?.preencher(s);
};

const ressimular = async banco => {
  if (!ativaId.value) return;
  try {
    await simuladorApi.post(`simulacoes/${ativaId.value}/bancos/${banco}/ressimular`);
    await carregarDetalhe(ativaId.value);
    agendarPoll();
  } catch (e) {
    useAlert(`Falha ao ressimular ${labelBanco(banco)}`);
  }
};

const atualizarStatus = async banco => {
  if (!ativaId.value) return;
  try {
    await simuladorApi.post(
      `simulacoes/${ativaId.value}/bancos/${banco}/atualizar-status`
    );
    await carregarDetalhe(ativaId.value);
    agendarPoll();
  } catch (e) {
    useAlert(`Falha ao atualizar ${labelBanco(banco)}`);
  }
};

/* v46: a digitação é uma tela PRÓPRIA (DigitacaoPanel) — o SimuladorModal
   antigo NÃO é usado aqui (decisão do Kairo; ele será deletado depois). */
const digitacao = ref(null); // { banco, oferta }
const ultimoEnriquecimento = ref({}); // do botão Enriquecer (prefill do painel)

const aoEnriquecido = payload => {
  ultimoEnriquecimento.value = payload?.dados || {};
};

const digitar = ({ banco, oferta } = {}) => {
  if (!detalhe.value?.simulacao) return;
  digitacao.value = { banco, oferta: oferta || null };
};

const fecharDigitacao = () => {
  digitacao.value = null;
  // proposta nova aparece na tela Propostas; aqui só sincroniza o detalhe
  if (ativaId.value) carregarDetalhe(ativaId.value).catch(() => {});
};

/* v47: no sucesso o painel FICA ABERTO mostrando código + link — fechar aqui
   foi o bug que escondeu o link do V8 na primeira digitação real do Kairo. */
const aoDigitacaoOk = () => {
  if (ativaId.value) carregarDetalhe(ativaId.value).catch(() => {});
};

onMounted(carregarLista);
onBeforeUnmount(pararPoll);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <div class="flex-1 px-6 py-6 overflow-y-auto">
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-1">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            Simulação e Digitação
          </h1>
          <p class="text-sm text-n-slate-11">
            Crie uma simulação por CPF e acompanhe a resposta de cada banco
            parceiro em tempo real.
          </p>
        </div>

        <div class="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div class="flex flex-col gap-3 lg:w-[360px] lg:shrink-0">
            <SimulacaoLista
              :simulacoes="simulacoes"
              :ativa-id="ativaId"
              :carregando="carregandoLista"
              @nova="novaSimulacao"
              @selecionar="selecionar"
              @limpar-todas="limparTodas"
              @busca="aoBusca"
            />
          </div>

          <div class="flex flex-col flex-1 min-w-0 gap-6">
            <!-- Card do cliente (aparece com a simulação ativa) -->
            <div
              v-if="detalhe?.simulacao"
              class="flex items-start justify-between gap-3 px-5 py-4 border rounded-lg border-n-weak bg-n-solid-2"
            >
              <div class="flex flex-col gap-1 min-w-0">
                <div class="flex items-center gap-2">
                  <h2 class="text-lg font-bold truncate text-n-slate-12">
                    {{ detalhe.simulacao.nome || 'Sem nome' }}
                  </h2>
                  <span
                    class="px-2 py-0.5 text-[10px] font-semibold uppercase rounded bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ detalhe.simulacao.produto }}
                  </span>
                </div>
                <p class="text-sm text-n-slate-11 tabular-nums">
                  {{ cpfFmt(detalhe.simulacao.cpf) }}
                  <template v-if="detalhe.simulacao.celular">
                    &nbsp;·&nbsp;{{ foneFmt(detalhe.simulacao.celular) }}
                  </template>
                </p>
              </div>
              <button type="button" class="sd-btn-ghost" @click="editarCliente">
                <span class="i-lucide-pencil size-4" />
                Editar
              </button>
            </div>

            <SimulacaoForm
              ref="form"
              :simulando="simulando"
              @simular="aoSimular"
              @caixa="carregarBancosDaCaixa"
              @enriquecido="aoEnriquecido"
            />
            <BancosPainel
              :bancos="detalhe?.bancos || []"
              :bancos-da-caixa="bancosDaCaixa"
              :simulacao-id="ativaId"
              @ressimular="ressimular"
              @atualizar-status="atualizarStatus"
              @digitar="digitar"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- v46: tela de digitação PRÓPRIA (nada do simulador antigo) -->
    <DigitacaoPanel
      v-if="digitacao"
      :banco="digitacao.banco"
      :oferta="digitacao.oferta || {}"
      :cliente="detalhe?.simulacao || {}"
      :enriquecimento="ultimoEnriquecimento"
      :simulacao-id="ativaId"
      @ok="aoDigitacaoOk"
      @close="fecharDigitacao"
    />
  </div>
</template>

<style>
/* NÃO é `scoped` de propósito: estas classes são usadas também pelos
   componentes filhos (lista, formulário e painel), e estilo `scoped` não
   atravessa para dentro deles. Todas levam o prefixo `sd-` para não colidir
   com nada do Chatwoot.
   As medidas espelham o original (altura 36px, raio 8px, labels 12px). */
.sd-label {
  @apply text-xs font-medium text-n-slate-11;
}
.sd-input {
  @apply w-full h-9 px-3 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 outline-none focus:border-n-brand;
}
.sd-chip {
  @apply px-3 py-1 text-xs font-medium transition-colors border rounded-full border-n-weak bg-n-solid-2 text-n-slate-11 hover:text-n-slate-12;
}
.sd-chip.is-on {
  @apply text-white border-transparent;
  background: #1f3ead;
}
.sd-vazio {
  @apply flex flex-col items-center justify-center gap-3 px-6 py-10 text-center border border-dashed rounded-lg border-n-weak bg-n-solid-2;
}
.sd-btn-primary {
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium text-white transition-colors rounded-md h-9 whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none;
  background: #1f3ead;
}
.sd-btn-primary:hover:not(:disabled) {
  background: #1b369a;
}
.sd-btn-ghost {
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium transition-colors rounded-md h-9 text-n-slate-12 hover:bg-n-alpha-2 whitespace-nowrap;
}
.sd-btn-amarelo {
  /* `text-black` nao existe no tema do Chatwoot (so `white` e os tokens n-*),
     por isso a cor do texto vai em CSS puro, fora do @apply. */
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium transition-colors rounded-md h-9 whitespace-nowrap disabled:opacity-50 disabled:pointer-events-none;
  background: #facc15;
  color: #1a1a1a;
}
.sd-btn-amarelo:hover:not(:disabled) {
  background: #eab308;
}

/* ---- v42: chips de resultado e botões dos cards ---- */
.sd-chip-oferta {
  @apply inline-flex items-center gap-2 px-3 py-1.5 text-xs transition-colors border rounded-lg;
  border-color: rgba(22, 163, 74, 0.55);
  background: rgba(22, 163, 74, 0.12);
  color: #4ade80;
}
.sd-chip-oferta:hover {
  background: rgba(22, 163, 74, 0.2);
}
.sd-chip-digitar {
  @apply px-1.5 py-0.5 text-[10px] font-medium rounded;
  background: rgba(22, 163, 74, 0.25);
}
.sd-chip-neutro {
  @apply inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-11;
}
.sd-chip-erro {
  @apply inline-flex items-center px-3 py-1.5 text-xs border rounded-lg bg-n-solid-2;
  border-color: rgba(220, 38, 38, 0.6);
  color: #f87171;
}
.sd-pill {
  @apply inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full;
  background: rgba(255, 255, 255, 0.18);
}
.sd-btn-card {
  @apply inline-flex items-center justify-center w-full gap-2 px-3 text-xs font-medium transition-colors border rounded-md h-8 border-n-weak text-n-slate-12 hover:bg-n-alpha-2;
}
.sd-btn-ressimular {
  @apply inline-flex items-center justify-center w-full gap-2 px-3 text-xs font-medium transition-colors rounded-md h-8 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2;
}
.sd-btn-digitar {
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium text-white transition-colors rounded-md h-9 whitespace-nowrap;
  background: #16a34a;
}
.sd-btn-digitar:hover:not(:disabled) {
  background: #15803d;
}
/* Digitar desligado (nenhum banco trava a digitação hoje — a regra fica para
   quem precisar). CSS puro para não depender de variante `disabled:` gerada
   no build. */
.sd-btn-digitar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ---- v44: linha de oferta (Simular amarelo · Copiar azul · Digitar verde) ---- */
.sd-btn-copiar {
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium text-white transition-colors rounded-md h-9 whitespace-nowrap;
  background: #1f3ead;
}
.sd-btn-copiar:hover {
  background: #1b369a;
}
.sd-btn-verde {
  @apply inline-flex items-center justify-center gap-2 px-4 text-sm font-medium text-white transition-colors rounded-md h-9 whitespace-nowrap disabled:opacity-50;
  background: #16a34a;
}
.sd-btn-verde:hover:not(:disabled) {
  background: #15803d;
}
.sd-badge-ambar {
  @apply inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md;
  background: rgba(217, 119, 6, 0.16);
  color: #fbbf24;
}
.sd-btn-copiar-link {
  @apply inline-flex items-center justify-center w-full gap-2 px-3 text-xs font-semibold transition-colors rounded-md h-8 text-white;
  background: #1f3ead;
}
.sd-btn-copiar-link:hover {
  background: #1b369a;
}

/* Card "Simular" expandido — estilo do outro simulador (borda verde
   tracejada, valor em verde, fatos em caixinhas). */
.sd-sim-card {
  @apply flex flex-col gap-3 p-4 border rounded-xl bg-n-solid-1;
  border-color: rgba(52, 211, 153, 0.5);
}
.sd-sim-fato {
  @apply flex flex-col items-center justify-center gap-0.5 px-3 py-2.5 border rounded-lg border-n-weak bg-n-alpha-black2;
}
.sd-sim-tag {
  @apply px-2.5 py-1 text-[10px] font-bold tracking-wide uppercase rounded-full;
  background: rgba(52, 211, 153, 0.16);
  color: #34d399;
}
.sd-sim-id {
  @apply px-3 py-2 text-xs border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-11;
}
.sd-sim-novo {
  @apply flex flex-col gap-2.5 p-3 border border-dashed rounded-lg border-n-weak;
}
.sd-sim-toggle {
  @apply inline-flex p-0.5 border rounded-lg border-n-weak bg-n-alpha-black2;
}
.sd-sim-toggle button {
  @apply px-3 py-1.5 text-xs font-medium rounded-md text-n-slate-11 transition-colors;
}
.sd-sim-toggle button.is-on {
  @apply text-white;
  background: #1f3ead;
}
.sd-sim-prazo {
  @apply px-3 py-1 text-xs font-semibold transition-colors border rounded-md border-n-weak bg-n-alpha-black2 text-n-slate-11 hover:text-n-slate-12;
}
.sd-sim-prazo.is-on {
  @apply text-white border-transparent;
  background: #e11d48;
}

/* Entrada do card Simular: desliza da esquerda enquanto os botoes da linha
   "chegam para o lado". */
.sd-slide-enter-active,
.sd-slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.sd-dig-secao {
  @apply text-sm font-bold tracking-wider uppercase text-n-teal-11;
}
.sd-slide-enter-from,
.sd-slide-leave-to {
  opacity: 0;
  transform: translateX(-0.75rem);
}
</style>
